// src/hooks/useMessages.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AxiosContext } from '../contextManager/axios.context';
import { endpoints } from '../constants/endpoints';
import { supabase } from '../supabaseClient'; // Import your Supabase client
import { useUser } from '../contextManager/user.context';
import { messageStorage } from '../utils/messageStorage';

export interface Message {
  id: string;
  created_at: string;
  connectionId: string;
  senderId: string;
  content: string;
  // status?: 'sending' | 'sent' | 'error'; 
  status?: 'pending' | 'failed';
  readAt?: string | null;
  replyTo?: {
    id: string;
    content: string;
    senderId: string;
  }
  _retryPayload?: SendMessagePayload;
}

export interface SendMessagePayload {
  connectionId: string;
  content: string;
  repliedToMessageId?: string; 
  optimisticReplyTo?: {
    id: string;
    content: string; 
    senderId: string; 
  }
}

// The hook now accepts the active connectionId as an argument
export const useMessages = (connectionId: string | null) => {
  const axios = useContext(AxiosContext);
  const queryClient = useQueryClient();
  const {userDetails} = useUser();
  const userId = userDetails?.id ?? '';
  const queryKey = ['messages', connectionId];

  const [localMessages, setLocalMessages] = useState<Message[]>(() => {
    if (!connectionId || !userId) return [];
    return messageStorage.getFailedMessages(userId, connectionId);
  })
  
  useEffect(() => {
    // Only run if we have both IDs
    if (connectionId && userId) {
      // 1. Go get the failed messages for THIS specific chat
      const savedMessages = messageStorage.getFailedMessages(userId, connectionId);
      
      // 2. Force the state to update, clearing out any old chat's junk
      setLocalMessages(savedMessages);
    }
  }, [connectionId, userId]); // <-- Runs whenever Chat ID or User ID changes

  // 1. QUERY: Fetch messages when a connection is selected
  const { data: serverMessages = [], isLoading } = useQuery<Message[]>({
    queryKey: queryKey,
    queryFn: async () => {
      const response = await axios.get(endpoints.getMessages(connectionId!));
      return response.data;
    },
    // Only run this query if a connectionId is provided
    enabled: !!connectionId,
  });

  const messages = useMemo(() => {
    const localIds = new Set(localMessages.map(m => m.id));
    const cleanServerMessages = serverMessages.filter(m => !localIds.has(m.id));
    
    const combined = [...cleanServerMessages, ...localMessages];
    return combined.sort((a,b) => new Date(a.created_at).getTime())

  }, [serverMessages, localMessages])

  const sendMessageMutation = useMutation({
    networkMode: 'always',
    retry: 0,
    mutationFn: (newMessage: SendMessagePayload) =>
      axios.post(endpoints.sendMessage, {
        connectionId: newMessage.connectionId,
        content: newMessage.content,
        repliedToMessageId: newMessage.repliedToMessageId,
      }),
    onMutate: async (newMessage) => {
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previousMessages = queryClient.getQueryData<Message[]>(queryKey) || [];

      // Create a temporary message to display immediately
      const optimisticMessage: Message = {
        id: `temp-${Date.now()}`,
        content: newMessage.content,
        senderId: userDetails!.id, // The current user is the sender
        connectionId: newMessage.connectionId,
        created_at: new Date().toISOString(),
        status: 'pending', 
        replyTo: newMessage.optimisticReplyTo,
        _retryPayload: newMessage,
    };

    setLocalMessages(prev => [...prev, optimisticMessage])
    messageStorage.addOrUpdateMessage(userId, optimisticMessage);
    queryClient.setQueryData<Message[]>(queryKey, [...previousMessages, optimisticMessage]);

    return { previousMessages, optimisticMessageId: optimisticMessage.id };
  },

  // If the mutation fails, use the context to roll back to the previous state
  onError: (err, newMessage, context) => {

    if (!context?.optimisticMessageId) return; 

    // unwind the cache
    if (context.previousMessages) {
      queryClient.setQueryData(queryKey, context.previousMessages);
    }

    // set local message to failure...
    setLocalMessages(prev => prev.map(msg => 
      msg.id === context.optimisticMessageId 
      ? {...msg, status: 'failed'}
      : msg
    ));

    // 
    // The robust code you were given:
    const messageToUpdateInStorage = {
      id: context.optimisticMessageId,
      content: newMessage.content, // Sourced from the mutation's variable
      senderId: userDetails!.id,
      connectionId: newMessage.connectionId,
      created_at: new Date().toISOString(), // Sourcing current time is the one small debatable part
      status: 'failed' as const,
      replyTo: newMessage.optimisticReplyTo,
      _retryPayload: newMessage // Sourcing the original full payload
    };

    messageStorage.addOrUpdateMessage(userId, messageToUpdateInStorage);

  },

  onSuccess: (data, variables, context) => {
    if (context?.optimisticMessageId) {
      setLocalMessages(prev => prev.filter(m => m.id !== context.optimisticMessageId));
    }
    messageStorage.removeMessage(userId, context.optimisticMessageId);
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['connections', userId] });
  }

});


const retryMessage = async (message: Message) => {
  if (!message._retryPayload) {
      console.error("Cannot retry message: Payload missing.");
      return;
  }

  // 1. Temporarily remove the failed message from the UI state
  setLocalMessages(prev => prev.filter(m => m.id !== message.id));
  messageStorage.removeMessage(userId, message.id);

  // 2. Trigger the mutation again with the preserved payload.
  // The onMutate logic will handle adding it back to the UI/Cache as 'pending'.
  sendMessageMutation.mutate(message._retryPayload);
};


const markAsReadMutation = useMutation({
  mutationFn: (connId: string) =>
    axios.post(endpoints.markMessagesAsRead, { connectionId: connId }),
  onSuccess: (data, connId) => { // 'connId' is the variable passed to the mutation
    // 1. Invalidate the messages for the specific chat that was just updated.
    queryClient.invalidateQueries({ queryKey: ['messages', connId] });

    // 2. ✅ THE FIX: Invalidate the main connections list to update the bold status.
    queryClient.invalidateQueries({ queryKey: ['connections', userDetails?.id] });
  },
  // You can also add onError handling for robustness
  onError: (error) => {
    console.error("Failed to mark messages as read:", error);
    // Optionally, revert any optimistic updates here or show a toast notification.
  }
});



  return {
    messages,
    isLoading,
    retryMessage,
    sendMessage: sendMessageMutation.mutateAsync,
    markMessagesAsRead: markAsReadMutation.mutate,
  };
};
